import React, { useEffect, useState, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  AlertTitle,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_URL, getConfig } from '../../../utils/config';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';

interface OAuthParams {
  code?: string;
  state?: string;
  realmId?: string;
  error?: string;
  error_description?: string;
}

interface ConnectionStatus {
  connected: boolean;
  companyId?: string;
  connectedAt?: string;
  lastUpdated?: string;
  refreshTokenExpiresAt?: string;
  message?: string;
}

/**
 * QuickBooks OAuth callback handler page
 * This page handles the OAuth redirect from QuickBooks after user authorization
 */
export const QuickBooksAuthPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'idle' | 'connected'>('loading');
  const [message, setMessage] = useState<string>('');
  const [oauthParams, setOauthParams] = useState<OAuthParams>({});
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
  const [reconnectReason, setReconnectReason] = useState<string | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);
  const isProcessingRef = useRef(false);
  const hasProcessedCodeRef = useRef(false);

  // Check connection status on mount
  useEffect(() => {
    const checkConnectionStatus = async () => {
      try {
        const response = await authenticatedFetch(`${API_URL}/api/quickbooks/status`);

        if (response && response.ok) {
          const data = await response.json();
          if (data.success && data.connected) {
            setConnectionStatus(data);
            setStatus('connected');
            setMessage('QuickBooks is connected');
          } else {
            setStatus('idle');
            setMessage('Ready to connect to QuickBooks');
            setReconnectReason(data.requiresReconnect && data.message ? data.message : null);
          }
        } else {
          setStatus('idle');
          setMessage('Ready to connect to QuickBooks');
        }
      } catch (error) {
        console.error('Error checking connection status:', error);
        setStatus('idle');
        setMessage('Ready to connect to QuickBooks');
      }
    };

    const handleAuthorizationCode = async (params: OAuthParams) => {
      // Create a unique key for this authorization code
      const codeKey = `qb_processed_${params.code}`;
      
      // IMPORTANT: Multiple safeguards to prevent duplicate API calls
      // React.StrictMode in development causes components to mount twice,
      // and OAuth authorization codes can only be used once.
      // We use both sessionStorage and refs to prevent duplicate processing.
      
      // Check if this specific code has already been processed
      if (sessionStorage.getItem(codeKey) === 'true') {
        console.log('Authorization code already processed (sessionStorage), skipping...');
        return;
      }
      
      // Double-check to prevent duplicate processing
      if (hasProcessedCodeRef.current || isProcessingRef.current) {
        console.log('Authorization code already processed or processing (ref), skipping...');
        return;
      }
      
      // Mark as processing to prevent duplicate calls
      isProcessingRef.current = true;
      hasProcessedCodeRef.current = true;
      sessionStorage.setItem(codeKey, 'true');
      
      try {
        // Use the same redirect URI that was used in the authorization request
        const redirectUri = `${window.location.origin}/admin/quickbooks/authsuccess`;

        const response = await authenticatedFetch(`${API_URL}/api/quickbooks/token`, {
          method: 'POST',
          body: JSON.stringify({
            code: params.code,
            realmId: params.realmId,
            state: params.state,
            redirectUri: redirectUri
          })
        });

        if (response) {
          const result = await response.json();

          if (result.success) {
            setStatus('success');
            setMessage('Successfully connected to QuickBooks!');
            // Clear URL params to prevent re-processing on refresh
            navigate('/admin/quickbooks/auth', { replace: true });
            // Clean up sessionStorage for successful completion
            sessionStorage.removeItem(codeKey);
            // Refresh connection status after successful connection
            setTimeout(() => {
              checkConnectionStatus();
            }, 1000);
          } else {
            // Clear the sessionStorage on error so user can retry
            sessionStorage.removeItem(codeKey);
            throw new Error(result.error || 'Failed to complete authorization');
          }
        } else {
          throw new Error('Failed to get response from server');
        }
      } catch (error) {
        setStatus('error');
        setMessage('Failed to complete authorization. Please try again.');
        console.error('Authorization error:', error);
        // Clear the sessionStorage on error so user can retry
        sessionStorage.removeItem(codeKey);
      } finally {
        isProcessingRef.current = false;
      }
    };

    // Extract OAuth parameters from URL
    const params: OAuthParams = {
      code: searchParams.get('code') || undefined,
      state: searchParams.get('state') || undefined,
      realmId: searchParams.get('realmId') || undefined,
      error: searchParams.get('error') || undefined,
      error_description: searchParams.get('error_description') || undefined,
    };
    
    setOauthParams(params);

    // Handle OAuth response
    if (params.error) {
      setStatus('error');
      setMessage(params.error_description || 'Authorization failed');
    } else if (params.code && !hasProcessedCodeRef.current && !isProcessingRef.current) {
      setStatus('loading');
      setMessage('Processing authorization...');
      handleAuthorizationCode(params);
    } else if (params.code && (hasProcessedCodeRef.current || isProcessingRef.current)) {
      // Code is already being processed or was processed, don't do anything
      console.log('OAuth code already being processed, skipping duplicate call');
    } else {
      // No OAuth params, check if already connected
      checkConnectionStatus();
    }
  }, [searchParams, authenticatedFetch, navigate]);

  const handleStartAuth = () => {
    const clientId = getConfig('VITE_QBOL_CLIENT_ID');
    
    // Handle different redirect URIs for development vs production
    const redirectUri = encodeURIComponent(`${window.location.origin}/admin/quickbooks/authsuccess`);
    
    const scope = 'com.intuit.quickbooks.accounting';
    const state = Math.random().toString(36).substring(7); // Generate random state for CSRF protection
    
    // Store state in sessionStorage for CSRF validation
    sessionStorage.setItem('qb_oauth_state', state);
    
    // OAuth authorization endpoint is the same for both sandbox and production
    const authUrl = `https://appcenter.intuit.com/connect/oauth2?client_id=${clientId}&scope=${scope}&redirect_uri=${redirectUri}&response_type=code&state=${state}`;
    
    // Redirect to QuickBooks OAuth
    window.location.href = authUrl;
  };

  const handleRetry = () => {
    setStatus('idle');
    setMessage('Ready to connect to QuickBooks');
    // Clear URL parameters
    navigate('/admin/quickbooks/auth', { replace: true });
  };

  const handleRevokeToken = async () => {
    setIsRevoking(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/quickbooks/revoke`, {
        method: 'POST'
      });

      if (response) {
        const result = await response.json();

        if (result.success) {
          setConnectionStatus(null);
          setStatus('idle');
          setMessage('QuickBooks connection has been revoked');
          // Clear any OAuth params
          navigate('/admin/quickbooks/auth', { replace: true });
        } else {
          throw new Error(result.error || 'Failed to revoke connection');
        }
      } else {
        throw new Error('Failed to get response from server');
      }
    } catch (error) {
      console.error('Error revoking token:', error);
      setMessage('Failed to revoke QuickBooks connection');
    } finally {
      setIsRevoking(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            QuickBooks Integration
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Connect your QuickBooks account to sync financial data with the Horwath HTL platform.
          </Typography>

          <Divider sx={{ my: 3 }} />

          {/* Status Display */}
          <Box sx={{ textAlign: 'center', py: 4 }}>
            {status === 'loading' && (
              <>
                <CircularProgress size={60} sx={{ mb: 3 }} />
                <Typography variant="h6" gutterBottom>
                  {message}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please wait while we complete the authorization process...
                </Typography>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 3 }} />
                <Alert severity="success" sx={{ mb: 3 }}>
                  <AlertTitle>Success!</AlertTitle>
                  {message}
                </Alert>
                {oauthParams.realmId && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Company ID: {oauthParams.realmId}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/admin/hub')}
                >
                  Go to Admin Hub
                </Button>
              </>
            )}

            {status === 'error' && (
              <>
                <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 3 }} />
                <Alert severity="error" sx={{ mb: 3 }}>
                  <AlertTitle>Authorization Failed</AlertTitle>
                  {message}
                </Alert>
                {oauthParams.error && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Error code: {oauthParams.error}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={handleRetry}
                >
                  Try Again
                </Button>
              </>
            )}

            {status === 'connected' && connectionStatus && (
              <>
                <LinkIcon sx={{ fontSize: 60, color: 'success.main', mb: 3 }} />
                <Alert severity="success" sx={{ mb: 3 }}>
                  <AlertTitle>Connected</AlertTitle>
                  QuickBooks is currently connected and active.
                </Alert>
                
                <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    Connection Details
                  </Typography>
                  <List dense>
                    {connectionStatus.companyId && (
                      <ListItem>
                        <ListItemText 
                          primary={`Company ID: ${connectionStatus.companyId}`}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    )}
                    {connectionStatus.connectedAt && (
                      <ListItem>
                        <ListItemText 
                          primary={`Connected: ${new Date(connectionStatus.connectedAt).toLocaleDateString()}`}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    )}
                    {connectionStatus.refreshTokenExpiresAt && (
                      <ListItem>
                        <ListItemText 
                          primary={`Token expires: ${new Date(connectionStatus.refreshTokenExpiresAt).toLocaleDateString()}`}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Paper>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={handleStartAuth}
                    sx={{ px: 3, py: 1.5 }}
                  >
                    Reconnect
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<LinkOffIcon />}
                    onClick={handleRevokeToken}
                    disabled={isRevoking}
                    sx={{ px: 3, py: 1.5 }}
                  >
                    {isRevoking ? 'Revoking...' : 'Revoke Connection'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/admin/hub')}
                  >
                    Go to Admin Hub
                  </Button>
                </Box>
              </>
            )}

            {status === 'idle' && (
              <>
                <InfoIcon sx={{ fontSize: 60, color: 'info.main', mb: 3 }} />
                {reconnectReason && (
                  <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                    <AlertTitle>Connection Broken</AlertTitle>
                    {reconnectReason}
                  </Alert>
                )}
                <Typography variant="h6" gutterBottom>
                  Connect to QuickBooks
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Click the button below to authorize this application to access your QuickBooks data.
                </Typography>
                
                <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    What permissions will be requested?
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="• Read and write access to your QuickBooks accounting data"
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="• Ability to create and update invoices, customers, and transactions"
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="• Access to financial reports and company information"
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  </List>
                </Paper>

                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleStartAuth}
                  sx={{ px: 4, py: 1.5 }}
                >
                  Connect to QuickBooks
                </Button>
              </>
            )}
          </Box>

          {/* Debug Information (only in development) */}
          {process.env.NODE_ENV === 'development' && Object.keys(oauthParams).length > 0 && (
            <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="caption" component="div" fontWeight="bold" gutterBottom>
                Debug Information (Development Only):
              </Typography>
              <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace' }}>
                {JSON.stringify(oauthParams, null, 2)}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Additional Information */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          This integration is secured and follows OAuth 2.0 standards. 
          Your credentials are never stored directly by our application.
        </Typography>
      </Box>
    </Container>
  );
};